import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonComment } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Card } from "@shared/models/card-stack"
import type { CommentRecipient } from "@shared/models/comment"
import Patch from "@shared/models/patch-updater"
import { assign, partial } from "lodash-es"
import moment from "moment"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  planbookId: string
  cardStackId: string
  card: Card
  comment: {
    id: string
    commentText: string
    commenterId: string
    recipients: CommentRecipient[]
    mentionedIds: string[]
  }
  commenterName: string
  commenterEmail: string
}

export default function lessonCardAddCommentAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CARD_ADD_COMMENT",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  const cardPatch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.card.id,
  }).push("embedded", "attributes.commentIds", payload.comment.id).patch

  const commentPatch = Patch.create("comment", payload.comment.id)
    .isCreatePatch()
    .set("attributes.commenterId", payload.comment.commenterId)
    .set("attributes.recipients", payload.comment.recipients)
    .set("attributes.mentionedIds", payload.comment.mentionedIds)
    .set("attributes.commentText", payload.comment.commentText)
    .set("attributes.rootType", "planbook")
    .set("attributes.rootId", payload.planbookId)
    .set("attributes.primaryObjectType", "card-stack")
    .set("attributes.primaryObjectId", payload.cardStackId)
    .set("attributes.adverbialObjectType", "card")
    .set("attributes.adverbialObjectId", payload.card.id).patch

  return [commentPatch, cardPatch]
}

function undoPatches(payload: Payload) {
  const cardPatch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.card.id,
  }).pull("embedded", "attributes.commentIds", payload.comment.id).patch

  const commentPatch = Patch.create("comment", payload.comment.id).set(
    "attributes.deletedAt",
    moment().toISOString()
  ).patch

  return [cardPatch, commentPatch]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)

  let cardTitle = "title" in payload.card.attributes ? payload.card.attributes.title : null

  let context = defaultLessonContext(payload, models)
  let newContext = assign({}, context, {
    objectType: "card",
    cardTitle: cardTitle,
    commentId: payload.comment.id,
    commenterName: payload.commenterName,
    commenterEmail: payload.commenterEmail,
    courseTitle: models.course?.attributes.title,
    dateString: models.dateStringForCardStack,
    planbookOwnerId: models.planbook?.relationships.owner.data.id,
  })
  let newEvent: LessonComment = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    primary_object_id: payload.cardStackId,
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "comment",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "cards",
    adverbial_verb: "adding",
    adverbial_object_type: "comment",
    adverbial_object_specifier: null,
    adverbial_object_id: payload.comment.id,
    adverbial_object_id_and_ancestor_ids: [...models.cardStackIdAndAncestorIds, payload.comment.id],
  }

  return {
    title: "Added a comment",
    titleForAnalytics: "Comment - Add",
    description: `${payload.commenterName} added a comment to the "${cardTitle}" card`,
    context: newContext,
    activity: defaultLessonActivity(payload, models, "created"),
    newAnalyticsEvents: [newEvent],
  }
}
